/**
 * A tab that outlives a deploy asks for chunks the deploy deleted: each build
 * renames them by content hash, so `/assets/index-<old hash>.js` is a 404 and
 * the dynamic import rejects. Seen on prod 2026-10-23: Parental Controls opened
 * blank on a pre-train-16 tab; a refresh fixed it.
 *
 * The fix is the refresh, done for the reader, once. A session flag stops a
 * loop: if the reloaded page fails again (a broken deploy, not a stale tab),
 * nothing reloads and `ChunkErrorBoundary` shows a small Reload card instead.
 * The flag clears once a load has stayed up for `STALE_CHUNK_STABLE_MS`, so a
 * second deploy later in the same tab is recovered the same way.
 *
 * Native builds serve their chunks from the app bundle and cannot go stale;
 * the guard is harmless there.
 */

export const STALE_CHUNK_FLAG = "wbm.staleChunkReload";
/** How long a load must stay up, with no stale-chunk failure, to count as successful. */
export const STALE_CHUNK_STABLE_MS = 10_000;

export interface StaleChunkEnv {
  storage: () => Storage;
  reload: () => void;
  /** False when the browser knows it is offline. */
  online?: () => boolean;
}

const browserEnv: StaleChunkEnv = {
  storage: () => window.sessionStorage,
  reload: () => window.location.reload(),
  online: () => navigator.onLine !== false,
};

// Chromium, WebKit, Firefox, and Vite's own CSS preload failure.
const STALE_CHUNK_MESSAGE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i;

export function isStaleChunkError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  return STALE_CHUNK_MESSAGE.test(message);
}

let reloadPending = false;

/** True between a reload being asked for and the page going away. */
export function isStaleChunkReloadPending(): boolean {
  return reloadPending;
}

/**
 * Reload once for a stale chunk. Returns false, and does nothing, when this
 * session already reloaded for one (or storage is unavailable, so a loop could
 * not be ruled out): the caller shows the Reload card. Offline, the same error
 * means the network dropped, not a deploy, and a reload would only trade the
 * app for the browser's offline page.
 */
export function reloadForStaleChunk(env: StaleChunkEnv = browserEnv): boolean {
  if (reloadPending) return true;
  if (env.online && !env.online()) return false;
  try {
    const storage = env.storage();
    if (storage.getItem(STALE_CHUNK_FLAG) !== null) return false;
    storage.setItem(STALE_CHUNK_FLAG, String(Date.now()));
  } catch {
    return false;
  }
  reloadPending = true;
  env.reload();
  return true;
}

export function clearStaleChunkFlag(env: StaleChunkEnv = browserEnv): void {
  try {
    env.storage().removeItem(STALE_CHUNK_FLAG);
  } catch {
    // No storage, no flag to clear.
  }
}

/** The Reload card's button: the reader asked, so the next stale chunk may auto-reload again. */
export function reloadByHand(env: StaleChunkEnv = browserEnv): void {
  clearStaleChunkFlag(env);
  reloadPending = true;
  env.reload();
}

let installed = false;

/**
 * Call once at boot. Vite sends every failed dynamic import (a dependency or
 * the module itself) through `vite:preloadError`; the first one reloads, and
 * any later one is left to throw into `ChunkErrorBoundary`. Returns an
 * uninstall for tests.
 */
export function installStaleChunkReload(
  env: StaleChunkEnv = browserEnv,
  target: Window = window,
): () => void {
  if (installed) return () => {};
  installed = true;
  let failedThisLoad = false;
  const onPreloadError = (event: Event) => {
    failedThisLoad = true;
    if (reloadForStaleChunk(env)) event.preventDefault();
  };
  target.addEventListener("vite:preloadError", onPreloadError);
  const stable = setTimeout(() => {
    if (!failedThisLoad && !reloadPending) clearStaleChunkFlag(env);
  }, STALE_CHUNK_STABLE_MS);
  return () => {
    target.removeEventListener("vite:preloadError", onPreloadError);
    clearTimeout(stable);
    installed = false;
  };
}

export function __resetStaleChunkForTest(): void {
  reloadPending = false;
  installed = false;
}
